import { useState, useEffect, useMemo, useCallback } from 'react';
import { crmService } from '../../services/crmService';
import OrgLogoUpload from './OrgLogoUpload';

const TYPE_META = {
  dod_military_service:   { label: 'Military Service',  color: '#22D3EE' },
  dod_agency:             { label: 'DoD Agency',         color: '#8B5CF6' },
  dod_combatant_command:  { label: 'Combatant Command',  color: '#F59E0B' },
  federal_agency:         { label: 'Federal Agency',     color: '#3B82F6' },
  legislative_senate:     { label: 'Senate',             color: '#10B981' },
  legislative_house:      { label: 'House',              color: '#10B981' },
  executive_office:       { label: 'Executive Office',   color: '#EC4899' },
  regulatory:             { label: 'Regulatory',         color: '#EF4444' },
  state_government:       { label: 'State Govt',         color: '#6B7280' },
  prime_contractor:       { label: 'Prime',              color: '#6B7280' },
  subcontractor:          { label: 'Subcontractor',      color: '#6B7280' },
  competitor:             { label: 'Competitor',         color: '#6B7280' },
  partner:                { label: 'Partner',            color: '#c4a563' },
  academia:               { label: 'Academia',           color: '#14B8A6' },
  think_tank:             { label: 'Think Tank',         color: '#6B7280' },
  ngo:                    { label: 'NGO',                color: '#6B7280' },
  foreign_government:     { label: 'Foreign Govt',       color: '#6B7280' },
};

const BRANCHES = ['army','navy','air_force','marine_corps','space_force','coast_guard','national_guard','joint','dod_civilian','civilian'];
const USC_TITLES = [6, 10, 14, 15, 22, 31, 32, 41, 42, 47, 49, 50];

const uscBadgeStyle = (title) => ({
  fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8, whiteSpace: 'nowrap',
  color: title === 10 ? '#22D3EE' : title === 50 ? '#c4a563' : title === 6 ? '#10B981' : '#94A3B8',
  border: `1px solid ${title === 10 ? '#22D3EE44' : title === 50 ? '#c4a56344' : title === 6 ? '#10B98144' : '#1E2D45'}`,
  background: title === 10 ? '#22D3EE10' : title === 50 ? '#c4a56310' : title === 6 ? '#10B98110' : 'transparent',
});

const inp = { background: '#0A0F1C', border: '1px solid #1E2D45', color: '#F1F5F9', padding: '6px 10px', borderRadius: 4, fontSize: 12, width: '100%', boxSizing: 'border-box' };
const lbl = { color: '#94A3B8', fontSize: 11, display: 'block', marginBottom: 3 };

function OrgModal({ org, parentId, orgs, childrenMap, onClose, onSaved }) {
  const isEdit = !!org?.id;
  const [form, setForm] = useState(() => ({
    name: org?.name || '', acronym: org?.acronym || '', type: org?.type || 'dod_agency',
    parent_org_id: org?.parent_org_id ?? parentId ?? '', service_branch: org?.service_branch || '',
    usc_title: org?.usc_title || '', usc_citation: org?.usc_citation || '',
    authority_summary: org?.authority_summary || '', website: org?.website || '',
    priority_level: org?.priority_level || 3, notes: org?.notes || '',
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Exclude self + descendants from parent options (prevents cycles)
  const excluded = useMemo(() => {
    const ex = new Set();
    if (isEdit) {
      const walk = (id) => { ex.add(id); (childrenMap.get(id) || []).forEach(c => walk(c.id)); };
      walk(org.id);
    }
    return ex;
  }, [isEdit, org, childrenMap]);

  const parentOptions = useMemo(() => {
    const opts = [];
    const walk = (list, depth) => {
      for (const o of list) {
        if (!excluded.has(o.id)) opts.push({ id: o.id, label: `${'  '.repeat(depth)}${o.acronym || o.name}` });
        walk(childrenMap.get(o.id) || [], depth + 1);
      }
    };
    walk(orgs.filter(o => !o.parent_org_id), 0);
    return opts;
  }, [orgs, childrenMap, excluded]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name required'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        ...(isEdit ? org : {}),
        ...form,
        parent_org_id: form.parent_org_id ? parseInt(form.parent_org_id) : null,
        usc_title: form.usc_title ? parseInt(form.usc_title) : null,
        service_branch: form.service_branch || null,
        priority_level: parseInt(form.priority_level) || 3,
      };
      const saved = isEdit
        ? await crmService.updateOrganization(org.id, payload)
        : await crmService.createOrganization(payload);
      onSaved(saved);
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${form.name}"? This cannot be undone.`)) return;
    setSaving(true); setError('');
    try {
      await crmService.deleteOrganization(org.id);
      onSaved(null); // Signal deletion
      onClose();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#111827', border: '1px solid #1E2D45', borderRadius: 8, width: 620, maxHeight: '85vh', display: 'flex', flexDirection: 'column', color: '#F1F5F9' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #1E2D45', display: 'flex', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: 14, color: '#22D3EE' }}>{isEdit ? 'Edit Organization' : 'Add Organization'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: 18 }}>×</button>
        </div>
        <form onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
          <div style={{ marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid #1E2D45' }}>
            <OrgLogoUpload
              orgId={isEdit ? org.id : null}
              currentLogo={org?.logo_url || null}
              onLogoUpdated={(logo) => { /* Already saved to DB */ }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 10 }}>
            <div>
              <label style={lbl}>Name *</label>
              <input style={inp} value={form.name} onChange={e => set('name', e.target.value)} required />
            </div>
            <div>
              <label style={lbl}>Acronym</label>
              <input style={inp} value={form.acronym} onChange={e => set('acronym', e.target.value)} placeholder="e.g. SOCOM" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 10 }}>
            <div>
              <label style={lbl}>Type</label>
              <select style={inp} value={form.type} onChange={e => set('type', e.target.value)}>
                {Object.entries(TYPE_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Parent Organization</label>
              <select style={inp} value={form.parent_org_id || ''} onChange={e => set('parent_org_id', e.target.value)}>
                <option value="">— None (top level) —</option>
                {parentOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Service Branch</label>
              <select style={inp} value={form.service_branch} onChange={e => set('service_branch', e.target.value)}>
                <option value="">— None —</option>
                {BRANCHES.map(b => <option key={b} value={b}>{b.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Priority (1 = highest)</label>
              <select style={inp} value={form.priority_level} onChange={e => set('priority_level', e.target.value)}>
                {[1,2,3,4,5].map(n => <option key={n} value={n}>P{n}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>USC Title</label>
              <select style={inp} value={form.usc_title} onChange={e => set('usc_title', e.target.value)}>
                <option value="">— None —</option>
                {USC_TITLES.map(t => <option key={t} value={t}>Title {t}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Establishing Citation</label>
              <input style={inp} value={form.usc_citation} onChange={e => set('usc_citation', e.target.value)} placeholder="e.g. 10 USC 167" />
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            <label style={lbl}>Authority Summary</label>
            <textarea style={{ ...inp, height: 70, resize: 'vertical' }} value={form.authority_summary} onChange={e => set('authority_summary', e.target.value)} placeholder="What this office is empowered to do, and why it matters for BD" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, marginTop: 10 }}>
            <div>
              <label style={lbl}>Website</label>
              <input style={inp} value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://" />
            </div>
            <div>
              <label style={lbl}>Notes</label>
              <textarea style={{ ...inp, height: 60, resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} />
            </div>
          </div>
          {error && <div style={{ color: '#EF4444', fontSize: 12, marginTop: 8 }}>{error}</div>}
        </form>
        <div style={{ padding: '10px 18px', borderTop: '1px solid #1E2D45', display: 'flex', gap: 8, justifyContent: 'space-between' }}>
          {isEdit && (
            <button onClick={handleDelete} disabled={saving} style={{ padding: '6px 14px', background: '#EF444422', border: '1px solid #EF4444', color: '#EF4444', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
              Delete
            </button>
          )}
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            <button onClick={onClose} style={{ padding: '6px 14px', background: '#111827', border: '1px solid #1E2D45', color: '#94A3B8', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>Cancel</button>
            <button onClick={handleSubmit} disabled={saving} style={{ padding: '6px 14px', background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 'bold', fontSize: 12 }}>
              {saving ? 'Saving…' : isEdit ? 'Save' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function GovLandscape() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | 10 | 50 | sof
  const [modal, setModal] = useState(null);   // { org } | { parentId }
  const [booted, setBooted] = useState(false);

  const load = useCallback(() => {
    return crmService.getLandscape().then(setOrgs).catch(console.error);
  }, []);

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);

  const byId = useMemo(() => new Map(orgs.map(o => [o.id, o])), [orgs]);
  const childrenMap = useMemo(() => {
    const m = new Map();
    for (const o of orgs) {
      if (o.parent_org_id && byId.has(o.parent_org_id)) {
        if (!m.has(o.parent_org_id)) m.set(o.parent_org_id, []);
        m.get(o.parent_org_id).push(o);
      }
    }
    return m;
  }, [orgs, byId]);
  const roots = useMemo(() => orgs.filter(o => !o.parent_org_id || !byId.has(o.parent_org_id)), [orgs, byId]);

  // Default: expand top-level branches once data arrives
  useEffect(() => {
    if (!booted && roots.length > 0) {
      setExpanded(new Set(roots.map(r => r.id)));
      setBooted(true);
    }
  }, [roots, booted]);

  // SOF subtree = USSOCOM + descendants
  const sofIds = useMemo(() => {
    const socom = orgs.find(o => o.seed_key === 'socom' || o.acronym === 'USSOCOM');
    const ids = new Set();
    if (socom) {
      const walk = (id) => { ids.add(id); (childrenMap.get(id) || []).forEach(c => walk(c.id)); };
      walk(socom.id);
    }
    return ids;
  }, [orgs, childrenMap]);

  // Homeland = DHS subtree + anything else carrying Title 6 (oversight committees)
  const dhsIds = useMemo(() => {
    const dhs = orgs.find(o => o.seed_key === 'dhs' || o.acronym === 'DHS');
    const ids = new Set();
    if (dhs) {
      const walk = (id) => { ids.add(id); (childrenMap.get(id) || []).forEach(c => walk(c.id)); };
      walk(dhs.id);
    }
    return ids;
  }, [orgs, childrenMap]);

  // Visible set under search/filter: matches + their ancestors
  const visibleIds = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q && filter === 'all') return null; // null = everything visible
    const matches = orgs.filter(o => {
      if (q && !(`${o.name} ${o.acronym || ''}`.toLowerCase().includes(q))) return false;
      if (filter === '10' && o.usc_title !== 10) return false;
      if (filter === '50' && o.usc_title !== 50) return false;
      if (filter === 'sof' && !sofIds.has(o.id)) return false;
      if (filter === 'homeland' && !dhsIds.has(o.id) && o.usc_title !== 6) return false;
      return true;
    });
    const vis = new Set();
    for (const m of matches) {
      let cur = m;
      while (cur) { vis.add(cur.id); cur = cur.parent_org_id ? byId.get(cur.parent_org_id) : null; }
    }
    return vis;
  }, [orgs, search, filter, sofIds, dhsIds, byId]);

  const filterActive = visibleIds !== null;

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    let stale = false;
    crmService.getOrganization(selectedId)
      .then(d => { if (!stale) setDetail(d); })
      .catch(() => { if (!stale) setDetail(null); });
    return () => { stale = true; };
  }, [selectedId, orgs]);

  const toggle = (id) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const selectOrg = (id) => {
    setSelectedId(id);
    // expand ancestors so selection is visible in the tree
    setExpanded(prev => {
      const next = new Set(prev);
      let cur = byId.get(id);
      while (cur && cur.parent_org_id) { next.add(cur.parent_org_id); cur = byId.get(cur.parent_org_id); }
      return next;
    });
  };

  const handleSaved = async (saved) => {
    setModal(null);
    await load();
    if (saved?.id) selectOrg(saved.id);
  };

  const renderNode = (org, depth) => {
    if (filterActive && !visibleIds.has(org.id)) return null;
    const kids = childrenMap.get(org.id) || [];
    const open = filterActive || expanded.has(org.id);
    const meta = TYPE_META[org.type] || { color: '#6B7280' };
    const isSel = org.id === selectedId;
    return (
      <div key={org.id}>
        <div
          onClick={() => selectOrg(org.id)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px 5px ' + (10 + depth * 16) + 'px',
            cursor: 'pointer', background: isSel ? '#22D3EE14' : 'transparent',
            borderLeft: isSel ? '2px solid #22D3EE' : '2px solid transparent',
          }}
        >
          {kids.length > 0 ? (
            <span
              onClick={e => { e.stopPropagation(); toggle(org.id); }}
              style={{
                width: 18,
                color: '#22D3EE',
                fontSize: 13,
                flexShrink: 0,
                userSelect: 'none',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 2,
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#22D3EE22'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >{open ? '▾' : '▸'}</span>
          ) : <span style={{ width: 18, flexShrink: 0 }} />}
          {org.logo_url ? (
            <img
              src={org.logo_url}
              alt={org.acronym || org.name}
              style={{ width: 16, height: 16, objectFit: 'contain', flexShrink: 0, borderRadius: 2 }}
            />
          ) : (
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: meta.color, flexShrink: 0 }} />
          )}
          <span style={{ fontSize: 12, fontWeight: 600, color: '#F1F5F9', whiteSpace: 'nowrap' }}>{org.acronym || org.name}</span>
          {org.acronym && (
            <span style={{ fontSize: 10, color: '#4B5563', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }} title={org.name}>{org.name}</span>
          )}
          {!org.acronym && <span style={{ flex: 1 }} />}
          {(org.contact_count > 0 || org.opportunity_count > 0) && (
            <span style={{ fontSize: 9, color: '#6B7280', flexShrink: 0 }}>
              {org.contact_count > 0 ? `${org.contact_count}c` : ''}{org.contact_count > 0 && org.opportunity_count > 0 ? ' · ' : ''}{org.opportunity_count > 0 ? `${org.opportunity_count}o` : ''}
            </span>
          )}
          {org.usc_title && <span style={uscBadgeStyle(org.usc_title)}>{org.usc_title} USC</span>}
        </div>
        {open && kids.map(k => renderNode(k, depth + 1))}
      </div>
    );
  };

  const selected = selectedId ? byId.get(selectedId) : null;
  const breadcrumb = useMemo(() => {
    if (!selected) return [];
    const chain = [];
    let cur = selected.parent_org_id ? byId.get(selected.parent_org_id) : null;
    while (cur) { chain.unshift(cur); cur = cur.parent_org_id ? byId.get(cur.parent_org_id) : null; }
    return chain;
  }, [selected, byId]);

  const acq = selected?.acquisition_authorities || [];
  const kids = selected ? (childrenMap.get(selected.id) || []) : [];
  const pill = (key, label) => (
    <button key={key} onClick={() => setFilter(key)} style={{
      padding: '4px 12px', borderRadius: 12, fontSize: 11, cursor: 'pointer',
      background: filter === key ? '#22D3EE18' : 'transparent',
      color: filter === key ? '#22D3EE' : '#6B7280',
      border: `1px solid ${filter === key ? '#22D3EE44' : '#1E2D45'}`,
      fontWeight: filter === key ? 600 : 400,
    }}>{label}</button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#0A0F1C' }}>
      {/* Toolbar */}
      <div style={{ padding: 'clamp(8px, 2vw, 10px) clamp(12px, 4vw, 16px)', borderBottom: '1px solid #1E2D45', background: '#111827', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search…"
          style={{ ...inp, flex: '1 1 150px', minWidth: '120px' }}
        />
        {pill('all', 'All')}
        {pill('10', 'T10')}
        {pill('50', 'T50')}
        {pill('sof', 'SOF')}
        {pill('homeland', 'HS')}
        <span style={{ fontSize: '9px', color: '#4B5563', whiteSpace: 'nowrap' }}>{orgs.length} orgs</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button onClick={() => setExpanded(new Set(orgs.map(o => o.id)))} style={{ background: 'none', border: '1px solid #1E2D45', color: '#6B7280', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontSize: '10px', whiteSpace: 'nowrap' }}>⬇</button>
          <button onClick={() => setExpanded(new Set(roots.map(r => r.id)))} style={{ background: 'none', border: '1px solid #1E2D45', color: '#6B7280', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontSize: '10px', whiteSpace: 'nowrap' }}>⬆</button>
          <button onClick={() => setModal({ parentId: null })} style={{ background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap' }}>+ Org</button>
        </div>
      </div>

      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94A3B8' }}>Loading landscape…</div>
      ) : (
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          <style>{`
            @media (max-width: 1024px) {
              .landscape-tree { display: ${selectedId ? 'none' : 'flex'}; }
              .landscape-detail { display: ${selectedId ? 'flex' : 'none'}; width: 100%; }
            }
            @media (min-width: 1025px) {
              .landscape-tree { flex: 0 0 460px; display: flex !important; }
              .landscape-detail { flex: 1; display: flex !important; }
            }
          `}</style>

          {/* Tree */}
          <div className="landscape-tree" style={{ flexDirection: 'column', borderRight: '1px solid #1E2D45', overflowY: 'auto', padding: '8px 0', width: '100%' }}>
            {roots.map(r => renderNode(r, 0))}
            {filterActive && roots.every(r => !visibleIds.has(r.id)) && (
              <div style={{ color: '#374151', fontSize: 12, textAlign: 'center', paddingTop: 30 }}>No matches</div>
            )}
          </div>

          {/* Detail */}
          <div className="landscape-detail" style={{ flexDirection: 'column', overflowY: 'auto', padding: '16px 20px', width: '100%' }}>
            {!selected ? (
              <div style={{ color: '#374151', fontSize: 13, paddingTop: 60, textAlign: 'center' }}>
                Select an organization to see its statutory authorities,<br />acquisition pathways, and your engagement footprint.
              </div>
            ) : (
              <>
                {/* Back button for mobile */}
                <button
                  onClick={() => setSelectedId(null)}
                  style={{
                    display: 'none', background: 'none', border: 'none',
                    color: '#6B7280', cursor: 'pointer', fontSize: '14px',
                    padding: '4px 0', marginBottom: '8px',
                  }}
                  className="mobile-back-btn"
                >
                  ← Back to list
                </button>
                <style>{`
                  @media (max-width: 1024px) {
                    .mobile-back-btn { display: inline-block !important; }
                  }
                `}</style>

                {breadcrumb.length > 0 && (
                  <div style={{ fontSize: 10, color: '#4B5563', marginBottom: 8 }}>
                    {breadcrumb.map((b, i) => (
                      <span key={b.id}>
                        <span onClick={() => selectOrg(b.id)} style={{ cursor: 'pointer', color: '#6B7280' }}>{b.acronym || b.name}</span>
                        {i < breadcrumb.length - 1 ? ' › ' : ''}
                      </span>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                  <h2 style={{ margin: 0, fontSize: 20, color: '#22D3EE' }}>{selected.acronym || selected.name}</h2>
                  {selected.acronym && <span style={{ fontSize: 13, color: '#94A3B8' }}>{selected.name}</span>}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8, border: '1px solid #1E2D45', color: TYPE_META[selected.type]?.color || '#6B7280' }}>
                    {TYPE_META[selected.type]?.label || selected.type}
                  </span>
                  {selected.service_branch && (
                    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 8, border: '1px solid #1E2D45', color: '#94A3B8' }}>{selected.service_branch.replace(/_/g, ' ')}</span>
                  )}
                  {selected.usc_title && <span style={uscBadgeStyle(selected.usc_title)}>Title {selected.usc_title}</span>}
                  <span style={{ fontSize: 10, color: '#6B7280' }}>P{selected.priority_level}</span>
                  {selected.website && (
                    <a href={selected.website} target="_blank" rel="noreferrer" style={{ fontSize: 10, color: '#22D3EE' }}>website ↗</a>
                  )}
                  <span style={{ flex: 1 }} />
                  <button onClick={() => setModal({ org: selected })} style={{ background: 'none', border: '1px solid #1E2D45', color: '#94A3B8', borderRadius: 4, padding: '4px 12px', cursor: 'pointer', fontSize: 11 }}>Edit</button>
                  <button onClick={() => setModal({ parentId: selected.id })} style={{ background: '#0D1F3C', border: '1px solid #22D3EE44', color: '#22D3EE', borderRadius: 4, padding: '4px 12px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>+ Sub-office</button>
                </div>

                {(selected.usc_citation || selected.authority_summary) && (
                  <div style={{ background: '#111827', border: '1px solid #1E2D45', borderRadius: 8, padding: '12px 14px', marginTop: 14 }}>
                    <div style={{ fontSize: 9, color: '#6B7280', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>Establishing Authority</div>
                    {selected.usc_citation && (
                      <div style={{ fontFamily: 'monospace', fontSize: 13, color: '#c4a563', marginBottom: 6 }}>{selected.usc_citation}</div>
                    )}
                    {selected.authority_summary && (
                      <div style={{ fontSize: 12, color: '#94A3B8', lineHeight: 1.6 }}>{selected.authority_summary}</div>
                    )}
                  </div>
                )}

                {acq.length > 0 && (
                  <div style={{ background: '#111827', border: '1px solid #1E2D45', borderRadius: 8, padding: '12px 14px', marginTop: 10 }}>
                    <div style={{ fontSize: 9, color: '#6B7280', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>Acquisition Authorities</div>
                    {acq.map((a, i) => (
                      <div key={i} style={{ marginBottom: i < acq.length - 1 ? 10 : 0, paddingBottom: i < acq.length - 1 ? 10 : 0, borderBottom: i < acq.length - 1 ? '1px solid #1E2D4566' : 'none' }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                          <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#22D3EE' }}>{a.cite}</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#F1F5F9' }}>{a.name}</span>
                        </div>
                        {a.note && <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{a.note}</div>}
                      </div>
                    ))}
                  </div>
                )}

                {kids.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 9, color: '#6B7280', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>Subordinate Organizations ({kids.length})</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {kids.map(k => (
                        <button key={k.id} onClick={() => selectOrg(k.id)} style={{
                          padding: '3px 10px', borderRadius: 10, fontSize: 11, cursor: 'pointer',
                          background: 'transparent', color: '#94A3B8', border: '1px solid #1E2D45',
                        }} title={k.name}>{k.acronym || k.name}</button>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 9, color: '#6B7280', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>
                    Contacts here ({detail?.contacts?.length ?? selected.contact_count ?? 0})
                  </div>
                  {(detail?.contacts || []).slice(0, 8).map(c => (
                    <div key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '4px 0', borderBottom: '1px solid #1E2D4544' }}>
                      <span style={{ fontSize: 12, color: '#F1F5F9', fontWeight: 600 }}>
                        {[c.rank_code || c.grade_code, c.first_name, c.last_name].filter(Boolean).join(' ')}
                      </span>
                      <span style={{ fontSize: 11, color: '#6B7280', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</span>
                      {c.authority_score != null && <span style={{ fontSize: 10, color: '#22D3EE' }}>{c.authority_score}</span>}
                    </div>
                  ))}
                  {(detail?.contacts || []).length === 0 && (
                    <div style={{ fontSize: 11, color: '#374151' }}>No contacts mapped to this office yet — add them from the Contacts tab.</div>
                  )}
                  {(detail?.contacts || []).length > 8 && (
                    <div style={{ fontSize: 10, color: '#6B7280', marginTop: 4 }}>+{detail.contacts.length - 8} more in the Contacts tab</div>
                  )}
                </div>

                {selected.opportunity_count > 0 && (
                  <div style={{ marginTop: 12, fontSize: 11, color: '#6B7280' }}>
                    {selected.opportunity_count} opportunit{selected.opportunity_count === 1 ? 'y' : 'ies'} with this customer — see Pipeline tab.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {modal && (
        <OrgModal
          org={modal.org}
          parentId={modal.parentId}
          orgs={orgs}
          childrenMap={childrenMap}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
